import React, { useState, useEffect } from 'react';
import TitleBar from './components/TitleBar';
import ServerSidebar from './components/ServerSidebar';
import ChannelSidebar from './components/ChannelSidebar';
import ChatArea from './components/ChatArea';
import MembersSidebar from './components/MembersSidebar';
import VoicePanel from './components/VoicePanel';
import Settings from './components/Settings';
import CommanderPanel from './components/CommanderPanel';
import CreateServerModal from './components/CreateServerModal';
import cloudflareAPI from './api/cloudflare';
import './App.css';

function App() {
  const [selectedServer, setSelectedServer] = useState(0);
  const [selectedChannel, setSelectedChannel] = useState({ type: 'text', id: 0 });
  const [showSettings, setShowSettings] = useState(false);
  const [showCreateServer, setShowCreateServer] = useState(false);
  const [settings, setSettings] = useState(null);
  const [servers, setServers] = useState([]);
  const [voiceConnected, setVoiceConnected] = useState(false);
  const [voiceChannel, setVoiceChannel] = useState(null);
  const [commanderActive, setCommanderActive] = useState(false);
  const [isCommander, setIsCommander] = useState(true);

  useEffect(() => {
    loadSettings();
    loadServers();
  }, []);

  const loadServers = async () => {
    try {
      const serverList = await cloudflareAPI.getServers();
      if (serverList && serverList.length > 0) {
        setServers(serverList);
      }
    } catch (error) {
      console.log('Using default servers (offline mode)');
    }
  };

  const handleCreateServer = async (serverData) => {
    try {
      const newServer = await cloudflareAPI.createServer(serverData.name, serverData.description);
      setServers(prev => [...prev, newServer]);
      setSelectedServer(newServer.id);
      setShowCreateServer(false);
    } catch (error) {
      console.error('Failed to create server:', error);
    }
  };

  const loadSettings = async () => {
    if (window.electronAPI) {
      const savedSettings = await window.electronAPI.getSettings();
      setSettings(savedSettings);
    } else {
      setSettings({
        username: 'User',
        status: 'online',
        theme: 'dark',
        notifications: true,
        soundEnabled: true,
        microphoneVolume: 80,
        speakerVolume: 100,
        inputDevice: 'default',
        outputDevice: 'default'
      });
    }
  };

  const saveSettings = async (newSettings) => {
    setSettings(newSettings);
    if (window.electronAPI) {
      await window.electronAPI.saveSettings(newSettings);
    }
  };

  const handleVoiceConnect = (channel) => {
    setVoiceConnected(true);
    setVoiceChannel(channel);
  };

  const handleVoiceDisconnect = () => {
    setVoiceConnected(false);
    setVoiceChannel(null);
  };

  if (!settings) {
    return (
      <div className="app loading">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="app">
      <TitleBar onOpenSettings={() => setShowSettings(true)} />
      <div className="app-content">
        <ServerSidebar 
          selectedServer={selectedServer} 
          onSelectServer={setSelectedServer}
          onOpenSettings={() => setShowSettings(true)}
          servers={servers}
          onCreateServer={() => setShowCreateServer(true)}
        />
        <ChannelSidebar 
          serverId={selectedServer}
          selectedChannel={selectedChannel}
          onSelectChannel={setSelectedChannel}
          onVoiceConnect={handleVoiceConnect}
          voiceChannel={voiceChannel}
          username={settings.username}
          status={settings.status}
          isCommander={isCommander}
          commanderActive={commanderActive}
          onCommanderToggle={() => setCommanderActive(!commanderActive)}
          onOpenSettings={() => setShowSettings(true)}
        />
        <ChatArea 
          serverId={selectedServer}
          channel={selectedChannel}
          username={settings.username}
        />
        <MembersSidebar serverId={selectedServer} />
        {isCommander && (
          <CommanderPanel 
            isActive={commanderActive}
            onToggle={() => setCommanderActive(!commanderActive)}
            username={settings.username}
            serverId={selectedServer}
          />
        )}
      </div>
      {voiceConnected && (
        <VoicePanel 
          channel={voiceChannel}
          onDisconnect={handleVoiceDisconnect}
          settings={settings}
        />
      )}
      {showSettings && (
        <Settings 
          settings={settings}
          onSave={saveSettings}
          onClose={() => setShowSettings(false)}
        />
      )}
      {showCreateServer && (
        <CreateServerModal
          isOpen={showCreateServer}
          onClose={() => setShowCreateServer(false)}
          onCreateServer={handleCreateServer}
        />
      )}
    </div>
  );
}

export default App;
